import React, { FC, useMemo, useState } from "react";
import { Button } from "@mui/material";
import __ from "src/utils/Translations";
import DownloadHistory, {
  ImportEntry,
} from "src/Pages/Company/OrganizationStructure/sharedComponents/DownloadHistory/DownloadHistory";
import {
  ContentType,
  DataSource,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/DataManagment/DataManagment.models";
import { handleDownload } from "src/Pages/Company/EmployeeManagement/DataManagment/Utils/handleDownload";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import { importAreaData } from "src/Pages/Company/OrganizationStructure/api";
import useImportData from "src/Pages/Company/OrganizationStructure/api/useImportData";
import useImportModal from "src/Pages/Company/OrganizationStructure/sharedComponents/useImportModal";
import {
  getAreaImportInstructions,
  getAreaUpdateFields,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/ImportExportAreaData/constants";
import ImportExportLayout from "src/Pages/Company/OrganizationStructure/Layouts/ImportExportLayout";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import useIsAhr from "src/utils/hooks/useIsAhr";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import BackButton from "src/Pages/Company/OrganizationStructure/sharedComponents/BackButton";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  organizationStructureAreaExport,
  organizationStructureAreaImport,
  organizationStructureAreaRead,
  organizationStructureLocalizationImportHistoryRead,
  organizationStructurePositionImportHistoryRead,
} from "src/utils/RoleBasedSecurity/permissions";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";

interface DataManagmentProps {
  structureId: string;
}

const ImportExportAreaData: FC<DataManagmentProps> = ({ structureId }) => {
  const companyId = useCompanyId();
  const isAhr = useIsAhr();
  const companyName = useCompanyName();
  const instructions = getAreaImportInstructions();
  const chipLabels = getAreaUpdateFields();

  const [processData, setProcessData] = useState<{ id: string } | undefined>(
    undefined
  );

  const { mutate: importData } = useImportData({
    mutationFn: (file: File) =>
      importAreaData({ companyId, structureId, file }),
    successMessage: __("Pomyślnie zaimportowano obszary"),
    errorMessage: __("Nie udało się zaimportować obszarów"),
    onImportSuccess: (data) => setProcessData(data),
  });

  const { open: openImportModal } = useImportModal({
    title: __("Import danych obszarów"),
    mutates: [importData],
  });

  const canExportAreaData = usePermission(organizationStructureAreaExport);
  const canImportAreaData = usePermission(organizationStructureAreaImport);
  const hasOrganizationStructurePositionImportHistoryRead = usePermission(
    organizationStructurePositionImportHistoryRead
  );
  const hasOrganizationStructureLocalizationImportHistoryRead = usePermission(
    organizationStructureLocalizationImportHistoryRead
  );
  const hasOrganizationStructureAreaRead = usePermission(
    organizationStructureAreaRead
  );
  const canPreviewImportHistory =
    hasOrganizationStructureAreaRead
    || hasOrganizationStructurePositionImportHistoryRead
    || hasOrganizationStructureLocalizationImportHistoryRead;

  const hasImportAreasEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_IMPORT_AREAS
  );
  const hasExportAreasEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_EXPORT_AREAS
  );

  const baseBreadcrumbs = (
    getCompanyBaseBreadcrumbs(companyId, companyName) as {
      title: string;
      link: string;
    }[]
  ).map(({ title, link }) => ({
    label: title,
    to: link,
  }));

  const breadcrumbs = isAhr
    ? [
        {
          label: `${__("Zarządzanie firmą")} ${companyName}`,
          to: `/ahr`,
        },
        {
          label: __("Import/Eksport i import obszarów"),
          to: `/ahr/organization-structure/import-export-area-data`,
        },
      ]
    : [
        ...baseBreadcrumbs,
        {
          label: __("Import/Eksport i import obszarów"),
          to: `/company/edit/${companyId}/organization-structure/import-export-area-data`,
        },
      ];

  const imports = useMemo<ImportEntry[]>(
    () => (processData ? [{ processData }] : []),
    [processData]
  );

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} />
      <BackButton sx={{ mt: 4, mb: 3, p: 0 }} />
      <ImportExportLayout>
        <ImportExportLayout.Header
          title={__("Eksport i import obszarów")}
          description={__(
            "Na tej stronie możesz eksportować dane dotyczące różnych obszarów w strukturze organizacji, edytować je w pliku Excel, a następnie importować zaktualizowane informacje z powrotem do systemu."
          )}
        />

        {canExportAreaData && hasExportAreasEnabled && (
          <>
            <ImportExportLayout.SectionHeader title={__("Eksport obszarów")} />
            <ImportExportLayout.ActionCard
              title={__("Eksportuj do pliku")}
              description={__("Eksportuj do pliku dane o obszarach.")}
              actions={
                <>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleDownload({
                        companyId,
                        structureId,
                        contentType: ContentType.CSV,
                        dataSource: DataSource.AREAS,
                      })
                    }
                  >
                    {__("Eksportuj plik CSV")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleDownload({
                        companyId,
                        structureId,
                        contentType: ContentType.XLSX,
                        dataSource: DataSource.AREAS,
                      })
                    }
                  >
                    {__("Eksportuj plik XLSX")}
                  </Button>
                </>
              }
            />

            <ImportExportLayout.Divider />
          </>
        )}

        <ImportExportLayout.SectionHeader title={__("Szablon do importów")} />
        <ImportExportLayout.ActionCard
          title={__("Pobierz szablon do importów")}
          description={__(
            "Pobrany szablon możesz wykorzystać do przygotowania importu obszarów"
          )}
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() =>
                  handleDownload({
                    companyId,
                    structureId,
                    contentType: ContentType.CSV,
                    dataSource: DataSource.AREAS,
                    template: true,
                  })
                }
              >
                {__("Pobierz szablon CSV")}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  handleDownload({
                    companyId,
                    structureId,
                    contentType: ContentType.XLSX,
                    dataSource: DataSource.AREAS,
                    template: true,
                  })
                }
              >
                {__("Pobierz szablon XLSX")}
              </Button>
            </>
          }
        />

        <ImportExportLayout.Divider />

        {canImportAreaData && hasImportAreasEnabled && (
          <>
            <ImportExportLayout.SectionHeader title={__("Import obszarów")} />
            <ImportExportLayout.Instructions instructions={instructions} />
            <ImportExportLayout.ActionCard
              title={__("Importuj obszary")}
              description={__("Wgraj plik .csv lub .xlsx")}
              actions={
                <Button variant="outlined" onClick={openImportModal}>
                  {__("Importuj")}
                </Button>
              }
              metadata={{
                label: __("Dane aktualizowane podczas tego importu"),
                chips: chipLabels,
              }}
            />
          </>
        )}

        {canPreviewImportHistory && <DownloadHistory imports={imports} />}
      </ImportExportLayout>
    </>
  );
};

export default ImportExportAreaData;
